import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { FormFieldConfig, FieldSelectedValue, FIELD_TYPES } from '../../../model/form.model';
import { CamelCasePipe } from '../../../utils/camel-case.pipe';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { ButtonNames, BUTTON_NAMES } from '../../../model/common.model';

@Component({
  selector: 'app-mat-text-field',
  standalone: true,
  imports: [MatInputModule, CommonModule, CamelCasePipe, ReactiveFormsModule, MatSelectModule, FormsModule, MatIconModule],
  templateUrl: './mat-text-field.component.html',
  styleUrl: './mat-text-field.component.scss',
})
export class MatTextFieldComponent implements OnInit {
  @Input() field!: FormFieldConfig;
  @Input() form!: FormGroup;
  @Input() buttonsArray: Array<{ name: string; isDisabled?: boolean }> = [];
  @Output() inputChanged = new EventEmitter<FieldSelectedValue>();
  @Output() actionButtonClicked = new EventEmitter<ButtonNames>();
  readonly ButtonDisplayValue: typeof BUTTON_NAMES = BUTTON_NAMES;
  ngOnInit() {
    this.field.selectedValue ??= '';
  }

  isCurrentFieldDisabled(): boolean {
    const control = this.form?.get(this.field?.fieldName);
    if (control) {
      return !!control.disabled;
    }
    return !!this.field?.disabled;
  }

  iconClick(field:FormFieldConfig){
    if(field.fieldActionConfig && !this.isCurrentFieldDisabled()){
      field.fieldActionConfig.click(field.selectedValue)
    }
  }

  onActionTrigger(event: any, triggerType: 'enter' | 'blur') {
    if (!this.field?.fieldActionConfig || this.isCurrentFieldDisabled()) {
      return;
    }

    const shouldTrigger =
      (triggerType === 'enter' && this.field.fieldActionConfig.triggerOnEnter) ||
      (triggerType === 'blur' && this.field.fieldActionConfig.triggerOnBlur);

    if (!shouldTrigger) {
      return;
    }

    const inputValue = event?.target?.value?.toString()?.trim?.() ?? '';
    this.field.selectedValue = inputValue;
    this.field.fieldActionConfig.click?.(inputValue);
  }
  onTextChange(FieldName: string, event: any): void {
    if (this.field) {
      const inputValue = (event.target.value).trim();
      this.field['selectedValue'] = inputValue;
      this.inputChanged.emit({
        FieldName: FieldName,
        Value: inputValue,
      });
    }
  }
  onKeypress(event: any) {
    if (this.field.fieldType === FIELD_TYPES.EMAIL) {
      return true;
    }
    const textPattern = new RegExp(this.field.pattern);
    const inputValue = event.target.value;
    const nextValue = inputValue + event.key;
    if (!textPattern.test(nextValue)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onPasteRestriction(e: any) {
    const key = e.clipboardData.getData('text');
    const trimmedKey = key?.toString()?.trim?.() ?? '';
    if (this.field?.pattern) {
      if (!new RegExp(this.field.pattern).test(trimmedKey)) {
        e.preventDefault();
        return false;
      }
    }
    const sendEvent = {
      target: {
        value: trimmedKey,
      },
    };
    this.onTextChange(this.field['fieldName'], sendEvent);
    return true;
  }
  
  private isButtonEnabled(buttonName: string): boolean {
    const button = this.buttonsArray?.find((btn) => btn.name?.toLowerCase() === buttonName.toLowerCase());
    return !!button && !button.isDisabled;
  }

  private hasButton(buttonName: string): boolean {
    return !!this.buttonsArray?.find((btn) => btn.name?.toLowerCase() === buttonName.toLowerCase());
  }

  private isTextSearchField(): boolean {
    return this.field?.fieldType === FIELD_TYPES.TEXTSEARCH;
  }

  showSearchSuffix(): boolean {
    if (this.field?.fieldActionConfig) {
      return false;
    }
    return this.isTextSearchField() && this.hasButton(BUTTON_NAMES.Search);
  }

  showResetSuffix(): boolean {
    this.syncFieldControlState();
    if (this.field?.fieldActionConfig) {
      return !!this.field.fieldActionConfig.resetClick && this.field.fieldActionConfig.resetVisible !== false;
    }
    return this.isTextSearchField() && this.hasButton(BUTTON_NAMES.Reset);
  }

  showFieldActionSearch(): boolean {
    if (!this.field?.fieldActionConfig) {
      return false;
    }
    return this.field.fieldActionConfig.searchVisible !== false;
  }
  
  onResetSuffixClick(): void {
    if (!this.field?.fieldActionConfig?.resetClick) {
      return;
    }
    this.field.selectedValue = '';
    this.form?.get(this.field.fieldName)?.setValue('', { emitEvent: false });
    this.inputChanged.emit({
      FieldName: this.field.fieldName,
      Value: '',
    });
    this.field.fieldActionConfig.resetClick();
  }
  
  private syncFieldControlState(): void {
    const fieldName = this.field?.fieldName;
    if (!fieldName) {return;}
    const control = this.form?.get(fieldName);
    if (!control) { return;}
    const shouldDisable = !!this.field?.disabled || this.field?.fieldActionConfig?.resetVisible === true;

    if (shouldDisable === control.disabled) {
      return;
    }
    if (shouldDisable) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

}
